import { defineStore } from 'pinia';
import { GetCategoryGamesDto } from '@/api/request/categories/get-category-games.dto';
import { CategoryGameDto } from '@/api/response/categories/category-game.dto';
import { type CategoryBaseResponse, client, getErrorMessage } from '@/api';
import { AddCategoryGamesDto } from '@/api/request/categories/add-category-games.dto';
import { DeleteCategoryGamesDto } from '@/api/request/categories/delete-category-games.dto';
import { SetCategoryGamesDto } from '@/api/request/categories/set-category-games.dto';
import { SetGameCategoriesDto } from '@/api/request/categories/set-game-categories.dto';
import { GetCategoryEntity } from '@/api/generated-client/models/GetCategoryEntity';
import { GetLocalesCategoryGameDto } from '@/api/request/categories/get-locales-category-game.dto';
import { LocalePositionDto } from '@/api/request/categories/locale-position.dto';
import { cacheCallback } from '@/lib/cache-callback';
import { CategoryPositionDto } from '@/api/request/categories/category-position.dto';
import { useLocales } from '@/hooks/useLocales';
import {
  ImportGamesToCategoryDto,
  ImportGamesToCategoryResponse,
} from '@/api/request/categories/import-games-to-category.dto';
import { TotalResponse } from '@/api/response/total.response';
import { ElMessage } from 'element-plus';
import { useLocalesStore } from '@/store/locales';
import { useUserStore } from '@/store/user';

type CategoryGamesState = {
  isGamesOpened: boolean;
  isAddGamesOpened: boolean;
  isImportGamesOpened: boolean;
  isGamePositionOpened: boolean;
  isGameCategoriesPositionOpened: boolean;
  isLinkCollectionCategoryOpened: boolean;
  loading: boolean;
  isDeleting: boolean;
  totalLoading: boolean;
  downloadLoading: boolean;
  error: string | null;
  localeId: number;
  category: GetCategoryEntity | null;
  game: CategoryGameDto | null;
};

export const useCategoryGamesStore = defineStore('categoryGames', {
  state: (): CategoryGamesState => ({
    isGamesOpened: false,
    isAddGamesOpened: false,
    isImportGamesOpened: false,
    isGamePositionOpened: false,
    isGameCategoriesPositionOpened: false,
    isLinkCollectionCategoryOpened: false,
    loading: false,
    isDeleting: false,
    totalLoading: false,
    downloadLoading: false,
    error: null,
    localeId: 0,
    category: null,
    game: null,
  }),
  actions: {
    openCategoryGames() {
      this.isGamesOpened = true;
    },
    openAddGames() {
      this.isAddGamesOpened = true;
    },
    openImportGames() {
      this.isImportGamesOpened = true;
    },

    openGamePosition() {
      this.isGamePositionOpened = true;
    },
    openGameCategoriesPosition() {
      this.isGameCategoriesPositionOpened = true;
    },
    openLinkCollectionCategory() {
      this.isLinkCollectionCategoryOpened = true;
    },

    closeCategoryGames() {
      this.isGamesOpened = false;
    },
    closeAddCategoryGames() {
      this.isAddGamesOpened = false;
    },
    closeImportGames() {
      this.isImportGamesOpened = false;
    },
    closeCategoryGamePosition() {
      this.isGamePositionOpened = false;
    },
    closeGameCategoriesPosition() {
      this.isGameCategoriesPositionOpened = false;
    },
    closeLinkCollectionCategory() {
      this.isLinkCollectionCategoryOpened = false;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setTotalLoading(value: boolean) {
      this.totalLoading = value;
    },
    setDownloadLoading(value: boolean) {
      this.downloadLoading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setCategory(category: GetCategoryEntity | null) {
      this.category = category;
    },
    setLocaleId(localeId: number) {
      this.localeId = localeId;
    },
    setGame(game: CategoryGameDto | null) {
      this.game = game;
    },

    getLocaleName() {
      const { locales } = useLocales();

      return locales?.value?.find((l) => l.id === this.localeId)?.name ?? 'Not Selected';
    },

    async getCategories(localeId?: number): Promise<CategoryBaseResponse[]> {
      return cacheCallback(async () => {
        this.setError(null);
        this.setLoading(true);

        let data: CategoryBaseResponse[] = [];

        const userStore = useUserStore();

        if (userStore.defaultLocaleId === localeId) {
          localeId = undefined;
        }

        try {
          data = await client.gamesCategories.getGameCategoriesBase(localeId);
        } catch (err) {
          this.setError('Error getting categories');
        } finally {
          this.setLoading(false);
        }

        return data;
      });
    },

    async getCategoryGames(dto: GetCategoryGamesDto): Promise<CategoryGameDto[]> {
      return cacheCallback(async () => {
        this.setError(null);
        this.setLoading(true);

        try {
          return await client.categoryGamesApi.getCategoryGames(dto);
        } catch (err) {
          this.setError('Error getting list of games by category and locale');
        } finally {
          this.setLoading(false);
        }

        return [];
      });
    },

    async getTotalCategoryGames(dto: GetCategoryGamesDto): Promise<TotalResponse> {
      return cacheCallback(async () => {
        this.setTotalLoading(true);

        try {
          return await client.categoryGamesApi.getTotalCategoryGames(dto);
        } catch (err) {
          this.setError('Error getting total games by category and locale');
        } finally {
          this.setTotalLoading(false);
        }

        return {
          total: 0,
          page: 1,
          perPage: 15,
          pageCount: 0,
        };
      });
    },

    async getCategoryGamesNotSelected(dto: GetCategoryGamesDto) {
      return cacheCallback(async () => {
        this.setError(null);
        this.setLoading(true);

        let data: CategoryGameDto[] = [];

        try {
          data = await client.categoryGamesApi.getCategoryGamesNotSelected(dto);
        } catch (err) {
          this.setError('Error getting list of games by category and locale');
        } finally {
          this.setLoading(false);
        }

        return data;
      });
    },

    async getCategoriesByGame(gameId: string, localeId: number): Promise<CategoryPositionDto[]> {
      return cacheCallback(async () => {
        this.setError(null);
        this.setLoading(true);

        let data: CategoryPositionDto[] = [];

        try {
          data = await client.categoryGamesApi.getCategoriesByGame(gameId, localeId);
        } catch (err) {
          this.setError('Error getting list of categories by game');
        } finally {
          this.setLoading(false);
        }

        return data;
      });
    },

    async getLocalesPositionsByCategoryGame(
      dto: GetLocalesCategoryGameDto,
    ): Promise<LocalePositionDto[]> {
      return cacheCallback(async () => {
        this.setError(null);
        this.setLoading(true);

        let data: LocalePositionDto[] = [];

        try {
          data = await client.categoryGamesApi.getLocalesPositionsByCategoryGame(dto);
        } catch (err) {
          this.setError('Error getting list of locales and their positions by category and game');
        } finally {
          this.setLoading(false);
        }

        return data;
      });
    },

    async addGameToCategory(dto: AddCategoryGamesDto) {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.categoryGamesApi.addGameToCategory(dto);
      } catch (err) {
        this.setError('Error adding games into category and locale');
      } finally {
        this.setLoading(false);
      }
    },

    async deleteGameFromCategory(dto: DeleteCategoryGamesDto) {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.categoryGamesApi.deleteGameFromCategory(dto);
      } catch (err) {
        this.setError('Error removing games from category');
      } finally {
        this.setLoading(false);
      }
    },

    async setGamesPositionByCategory(dto: SetCategoryGamesDto) {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.categoryGamesApi.setGamesPositionByCategory(dto);
      } catch (err) {
        this.setError('Error changing game position to category and locale');
      } finally {
        this.setLoading(false);
      }
    },

    async setGamePositionsForCategories(dto: SetGameCategoriesDto) {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.categoryGamesApi.setGamePositionsForCategories(dto);
      } catch (err) {
        this.setError('Error changing game position to categories');
      } finally {
        this.setLoading(false);
      }
    },

    async importGamesToCategory(
      dto: ImportGamesToCategoryDto,
    ): Promise<ImportGamesToCategoryResponse> {
      try {
        return await client.categoryGamesApi.importGamesToCategory(dto);
      } catch (err: any) {
        // if 400 status with body, return body as success response
        if (err?.status === 400 && err?.body) {
          return err.body;
        }
        throw err;
      }
    },

    async exportGamesFromCategory(dto: GetCategoryGamesDto): Promise<void> {
      this.setDownloadLoading(true);

      const localesStore = useLocalesStore();
      const fileName = `csv_export_all_games_locale_id_${localesStore.getLocaleName(dto.localeId)}`;

      try {
        await client.categoryGamesApi.exportGamesFromCategory(dto, fileName);
      } catch (err) {
        const message = getErrorMessage(err, 'Error during downloading games');

        ElMessage.error(message);
      } finally {
        this.setDownloadLoading(false);
      }
    },

    async deleteGamesFromCategory(items: CategoryGameDto[] = []) {
      if (items.length === 0) {
        return;
      }

      if (this.category === null) {
        return;
      }

      const categoryId = this.category.id;
      const gameIds = items.map((item) => item.gameId);

      this.isDeleting = true;

      try {
        await this.deleteGameFromCategory({
          gameIds,
          categoryId,
        });
      } catch (err) {
        this.setError('Error while deleting games from category');

        throw err;
      } finally {
        this.isDeleting = false;
      }
    },
  },
});
